/**
 * Утилиты для работы с изображениями
 * Автоматическое сжатие, изменение размера и обрезка
 */

import { getClientEnv, isSafariLike } from '@/utils/clientEnv';

interface ImageResizeOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
}

interface CropOptions {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Комментарий на русском: проверка поддержки кодирования нужного формата через canvas (например, WebP).
let _canvasEncodeSupport: Record<string, boolean> | null = null;
export function supportsCanvasEncodeMime(mime: string): boolean {
  const m = String(mime || '').trim().toLowerCase();
  if (!m) return false;
  if (typeof document === 'undefined') return false;
  try {
    if (!_canvasEncodeSupport) _canvasEncodeSupport = {};
    if (typeof _canvasEncodeSupport[m] === 'boolean') return _canvasEncodeSupport[m] as boolean;
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    const data = c.toDataURL(m);
    const ok = typeof data === 'string' && data.startsWith(`data:${m}`);
    _canvasEncodeSupport[m] = ok;
    return ok;
  } catch {
    try {
      if (_canvasEncodeSupport) _canvasEncodeSupport[m] = false;
    } catch {}
    return false;
  }
}

// ===== Поддержка HEIF/HEIC (конвертация в JPEG на клиенте) =====

function getFileExtension(name: string): string {
  const n = String(name || '').trim().toLowerCase();
  const i = n.lastIndexOf('.');
  if (i < 0) return '';
  return n.slice(i + 1).trim();
}

function isGifLikeFile(file: File): boolean {
  const t = String(file?.type || '').toLowerCase().trim();
  const ext = getFileExtension(String(file?.name || ''));
  return t === 'image/gif' || ext === 'gif';
}

export function isHeicLikeImage(file: File): boolean {
  const t = String(file?.type || '').toLowerCase().trim();
  const ext = getFileExtension(String(file?.name || ''));
  if (ext === 'heic' || ext === 'heif') return true;
  if (t === 'image/heic' || t === 'image/heif') return true;
  if (t === 'image/heic-sequence' || t === 'image/heif-sequence') return true;
  return false;
}

// Комментарий на русском: cache, чтобы не конвертировать один и тот же HEIC/HEIF несколько раз подряд.
const heicConvertCache = new WeakMap<File, Promise<File>>();

export async function convertHeicToJpeg(file: File, quality: number = 0.92): Promise<File> {
  const q = Number.isFinite(quality) ? Math.max(0.4, Math.min(1, quality)) : 0.92;
  if (typeof window === 'undefined') throw new Error('HEIC/HEIF conversion is only available in browser');
  // Комментарий на русском: динамический import — библиотека нужна только при выборе HEIC/HEIF.
  const mod: any = await import('heic2any');
  const heic2any = (mod && (mod.default || mod)) as any;
  if (typeof heic2any !== 'function') throw new Error('heic2any is not available');

  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: q });
  const blob: Blob | null = Array.isArray(out) ? (out[0] as any) : (out as any);
  if (!blob || typeof (blob as any).size !== 'number') throw new Error('HEIC/HEIF conversion failed');

  const base = (() => {
    const n = String(file.name || '').trim();
    const noExt = n.replace(/\.[^/.]+$/, '');
    return noExt || 'photo';
  })();
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}

export async function ensureCanvasReadableImageFile(file: File): Promise<File> {
  if (!file) throw new Error('Empty file');
  if (!isHeicLikeImage(file)) return file;
  const cached = heicConvertCache.get(file);
  if (cached) return cached;
  const p = convertHeicToJpeg(file).catch((e) => {
    // Комментарий на русском: если конвертация упала — не кэшируем навсегда ошибку.
    try {
      heicConvertCache.delete(file);
    } catch {}
    throw e;
  });
  heicConvertCache.set(file, p);
  return p;
}

/**
 * Автоматическое сжатие изображения
 */
export async function compressImage(
  file: File, 
  options: ImageResizeOptions = {}
): Promise<File> {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    format = 'jpeg'
  } = options;

  const srcFile = await ensureCanvasReadableImageFile(file);

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const objectUrl = URL.createObjectURL(srcFile);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      // Вычисляем новые размеры с сохранением пропорций
      let { width, height } = calculateNewDimensions(
        img.width, 
        img.height, 
        maxWidth, 
        maxHeight
      );

      canvas.width = width;
      canvas.height = height;

      // Рисуем изображение с новыми размерами
      ctx?.drawImage(img, 0, 0, width, height);

      // Конвертируем в Blob с качеством
      const targetMime = `image/${format}`;
      const fallbackMime = 'image/jpeg';
      const doToBlob = (mime: string) =>
        new Promise<Blob>((res, rej) => {
          try {
            canvas.toBlob(
              (b) => (b ? res(b) : rej(new Error('toBlob returned null'))),
              mime,
              mime === 'image/png' ? undefined : quality
            );
          } catch (e: any) {
            rej(e || new Error('toBlob failed'));
          }
        });

      void (async () => {
        try {
          let blob: Blob;
          try {
            blob = await doToBlob(targetMime);
          } catch {
            // Комментарий на русском: некоторые браузеры не умеют кодировать webp/avif через canvas.
            blob = await doToBlob(fallbackMime);
          }

          const outMime = String(blob.type || targetMime || fallbackMime).trim() || fallbackMime;
          const outFormat = outMime.split('/', 2)[1] || 'jpeg';
          const compressedFile = new File([blob], generateFileName(file.name || srcFile.name, outFormat), { type: outMime });
          resolve(compressedFile);
        } catch (e: any) {
          reject(e || new Error('Ошибка сжатия изображения'));
        }
      })();
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Ошибка загрузки изображения'));
    };
    img.src = objectUrl;
  });
}

/**
 * Обрезка изображения
 */
export async function cropImage(
  file: File, 
  cropOptions: CropOptions,
  resizeOptions: ImageResizeOptions = {}
): Promise<File> {
  const { x, y, width, height } = cropOptions;
  const { quality = 0.9, format = 'jpeg' } = resizeOptions;

  const srcFile = await ensureCanvasReadableImageFile(file);

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const objectUrl = URL.createObjectURL(srcFile);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      canvas.width = width;
      canvas.height = height;

      // Обрезаем изображение
      ctx?.drawImage(
        img,
        x, y, width, height, // Источник (область обрезки)
        0, 0, width, height  // Назначение (весь canvas)
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Ошибка обрезки изображения'));
            return;
          }

          const croppedFile = new File(
            [blob],
            generateFileName(file.name || srcFile.name, format, 'cropped'),
            { type: `image/${format}` }
          );

          resolve(croppedFile);
        },
        `image/${format}`,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Ошибка загрузки изображения'));
    };
    img.src = objectUrl;
  });
}

/**
 * Создание превью изображения
 */
export async function createImagePreview(file: File): Promise<string> {
  // Комментарий на русском: для HEIC/HEIF делаем конвертацию, иначе <img> в большинстве браузеров не покажет превью.
  const srcFile = await ensureCanvasReadableImageFile(file);
  // Используем objectURL, чтобы не раздувать память base64-строкой (особенно для PNG)
  return URL.createObjectURL(srcFile);
}

/**
 * Проверка, является ли файл изображением
 */
export function isImageFile(file: File): boolean {
  const t = String(file?.type || '').toLowerCase();
  if (t.startsWith('image/')) return true;
  const ext = getFileExtension(String(file?.name || ''));
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'heic', 'heif', 'bmp', 'dib', 'tif', 'tiff'].includes(ext);
}

/**
 * Получение размеров изображения
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return ensureCanvasReadableImageFile(file).then((srcFile) => new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    
    img.onerror = () => {
      reject(new Error('Ошибка загрузки изображения'));
      URL.revokeObjectURL(img.src);
    };
    
    img.src = URL.createObjectURL(srcFile);
  }));
}

/**
 * Вычисление новых размеров с сохранением пропорций
 */
function calculateNewDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let width = originalWidth;
  let height = originalHeight;

  // Проверяем, нужно ли уменьшать
  if (width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }

  if (height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }

  return { 
    width: Math.round(width), 
    height: Math.round(height) 
  };
}

/**
 * Генерация имени файла
 */
function generateFileName(
  originalName: string, 
  format: string, 
  suffix?: string
): string {
  const nameWithoutExt = originalName.split('.').slice(0, -1).join('.');
  const parts = [nameWithoutExt];
  
  if (suffix) {
    parts.push(suffix);
  }
  
  return `${parts.join('_')}.${format}`;
}

/**
 * Проверка размера файла
 */
export function checkFileSize(file: File, maxSizeMB: number = 10): boolean {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
}

/**
 * Форматирование размера файла для отображения
 */
export function formatFileSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Byte';
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  
  return `${Math.round(size * 100) / 100} ${sizes[i]}`;
}

/**
 * Автоматическая оптимизация изображения
 * Выбирает лучшие настройки в зависимости от размера и типа
 */
export async function optimizeImage(file: File): Promise<File> {
  if (!isImageFile(file)) {
    throw new Error('Файл не является изображением');
  }

  // Комментарий на русском: HEIC/HEIF конвертируем в JPEG перед любыми операциями (иначе canvas/Image часто не умеют).
  const srcFile = await ensureCanvasReadableImageFile(file);

  const dimensions = await getImageDimensions(srcFile);
  const fileSizeMB = srcFile.size / (1024 * 1024);
  const maxSide = Math.max(dimensions.width, dimensions.height);

  // Автоматический выбор настроек оптимизации
  let options: ImageResizeOptions = {
    quality: 0.85,
    format: 'jpeg'
  };

  // Для больших изображений (по размеру или по разрешению) — более агрессивное сжатие/уменьшение.
  if (fileSizeMB > 5 || maxSide > 5000) {
    options.maxWidth = 1920;
    options.maxHeight = 1080;
    options.quality = 0.7;
  } else if (fileSizeMB > 2 || maxSide > 3500) {
    options.maxWidth = 2560;
    options.maxHeight = 1440;
    options.quality = 0.8;
  } else {
    // Для маленьких файлов сохраняем исходные размеры
    // Но всё равно ограничиваем «экстремально большие» по разрешению изображения, чтобы не подвешивать вкладку
    options.maxWidth = Math.min(dimensions.width, 2560);
    options.maxHeight = Math.min(dimensions.height, 1440);
    options.quality = 0.9;
  }

  const mime = String(srcFile.type || '').toLowerCase();
  const ext = getFileExtension(String(srcFile.name || file.name || ''));
  const isPng = mime === 'image/png' || ext === 'png';
  const isWebp = mime === 'image/webp' || ext === 'webp';
  const canEncodeWebp = supportsCanvasEncodeMime('image/webp');

  // Комментарий на русском: PNG (особенно скриншоты) часто очень тяжелые.
  // В Telegram-подобном UX лучше переводить большие PNG в WebP (с альфой тоже ок) — это резко снижает размер.
  if (isPng) {
    if ((fileSizeMB > 0.9 || maxSide > 2600) && canEncodeWebp) {
      options.format = 'webp';
      options.quality = fileSizeMB > 4 ? 0.78 : 0.86;
    } else {
      // Комментарий на русском: если WebP encode недоступен — уходим в JPEG (иначе PNG может остаться слишком тяжелым).
      // Для маленьких PNG сохраняем PNG, чтобы не получить артефакты на иконках/скринах.
      if (fileSizeMB <= 0.7 && maxSide <= 1800) {
        options.format = 'png';
        options.quality = 0.95;
      } else {
        options.format = 'jpeg';
        options.quality = fileSizeMB > 4 ? 0.74 : 0.82;
      }
    }
  } else if (isWebp) {
    // Комментарий на русском: WebP пересобираем в WebP (если нужно resize).
    options.format = canEncodeWebp ? 'webp' : 'jpeg';
  }

  const out = await compressImage(srcFile, options);
  // Комментарий на русском: если компрессия не дала профита (иногда для PNG), возвращаем исходник.
  if (out && out.size > 0 && out.size >= srcFile.size && srcFile.size <= 1_500_000) return srcFile;
  return out;
}

async function imageHasTransparency(file: File): Promise<boolean> {
  const srcFile = await ensureCanvasReadableImageFile(file);
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(srcFile);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      try {
        const maxSide = 768;
        const ratio = Math.min(1, maxSide / Math.max(1, img.width, img.height));
        const width = Math.max(1, Math.round(img.width * ratio));
        const height = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          resolve(false);
          return;
        }
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const data = ctx.getImageData(0, 0, width, height).data;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) {
            resolve(true);
            return;
          }
        }
        resolve(false);
      } catch {
        resolve(false);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(false);
    };
    img.src = objectUrl;
  });
}

/**
 * Оптимизация изображений для текстур/иконок Mystery Room:
 * - сжимает через тот же canvas-пайплайн, что и админка;
 * - сохраняет альфа-канал: прозрачные PNG/WebP не переводятся в JPEG;
 * - GIF/SVG не трогает, чтобы не потерять анимацию/вектор.
 */
export async function optimizeImageForTextureUpload(file: File): Promise<File> {
  if (!isImageFile(file)) {
    throw new Error('Файл не является изображением');
  }

  const srcFile = await ensureCanvasReadableImageFile(file);
  const mime = String(srcFile.type || '').toLowerCase();
  const name = String(srcFile.name || file.name || '').toLowerCase();
  const isGif = mime === 'image/gif' || name.endsWith('.gif');
  const isSvg = mime === 'image/svg+xml' || name.endsWith('.svg');
  if (isGif || isSvg) {
    return srcFile;
  }

  const hasAlpha = await imageHasTransparency(srcFile);
  const canWebp = supportsCanvasEncodeMime('image/webp');
  const targetBytes = hasAlpha ? 900_000 : 700_000;
  const maxSide = hasAlpha ? 1536 : 1400;

  const candidates: File[] = [];
  const addCandidate = async (format: 'jpeg' | 'png' | 'webp', quality: number, side: number) => {
    try {
      const candidate = await compressImage(srcFile, {
        maxWidth: side,
        maxHeight: side,
        quality,
        format,
      });
      if (candidate?.size > 0) candidates.push(candidate);
    } catch {
      // Комментарий на русском: если браузер не смог закодировать формат, просто пробуем следующий вариант.
    }
  };

  if (hasAlpha) {
    if (canWebp) {
      await addCandidate('webp', 0.88, maxSide);
      await addCandidate('webp', 0.78, 1200);
      await addCandidate('webp', 0.68, 960);
    } else {
      await addCandidate('png', 0.95, maxSide);
      await addCandidate('png', 0.95, 1024);
    }
  } else {
    if (canWebp) {
      await addCandidate('webp', 0.84, maxSide);
      await addCandidate('webp', 0.74, 1100);
    }
    await addCandidate('jpeg', 0.82, maxSide);
    await addCandidate('jpeg', 0.72, 1100);
  }

  const best = candidates
    .filter((candidate) => candidate.size > 0)
    .sort((a, b) => {
      const aFits = a.size <= targetBytes ? 0 : 1;
      const bFits = b.size <= targetBytes ? 0 : 1;
      return aFits - bFits || a.size - b.size;
    })[0];

  if (!best) return srcFile;
  if (best.size >= srcFile.size && srcFile.size <= targetBytes) return srcFile;
  return best;
}

/**
 * Оптимизация изображения специально для отправки в чат:
 * - приводит HEIC/HEIF к JPEG
 * - кодирует в JPEG/WebP (в зависимости от исходника и поддержки браузера)
 * - подгоняет размер под целевой лимит (по байтам), чтобы не упираться в лимиты PHP upload_max_filesize/post_max_size
 */
export async function optimizeImageForChatUpload(
  file: File,
  options: {
    targetBytes?: number;
    maxWidth?: number;
    maxHeight?: number;
  } = {}
): Promise<File> {
  const env = getClientEnv();
  // Комментарий на русском:
  // Подстраиваем “вес” и максимальные размеры под устройство/ОС:
  // - на мобильных и Safari/iOS уменьшаем targetBytes и размеры (меньше трафика, меньше RAM/крашей)
  // - на десктопе можно держать чуть больше качества.
  const isMobileLike = env.device === 'mobile' || env.device === 'tablet';
  const safariLike = isSafariLike(env);

  const defaultTarget = safariLike ? 1_200_000 : isMobileLike ? 1_400_000 : 1_900_000;
  const defaultMaxW = safariLike ? 2048 : isMobileLike ? 2304 : 2560;
  const defaultMaxH = safariLike ? 2048 : isMobileLike ? 2304 : 1440;

  const targetBytes = Math.max(200_000, Math.round(Number(options.targetBytes || 0) || defaultTarget));
  const maxWidth = Math.max(320, Math.round(Number(options.maxWidth || 0) || defaultMaxW));
  const maxHeight = Math.max(320, Math.round(Number(options.maxHeight || 0) || defaultMaxH));

  const src0 = await ensureCanvasReadableImageFile(file);
  const t0 = String(src0.type || '').toLowerCase();
  const n0 = String(src0.name || '').toLowerCase();
  const isGif = t0 === 'image/gif' || n0.endsWith('.gif');
  if (isGif) {
    try {
      return await optimizeGifForChatUpload(src0, { targetBytes, maxWidth, maxHeight });
    } catch {
      return src0;
    }
  }

  // Базовая оптимизация
  let best: File;
  try {
    best = await optimizeImage(src0);
  } catch {
    // fallback, если optimizeImage не смог (например, странный формат)
    best = await compressImage(src0, { maxWidth, maxHeight, quality: 0.82, format: 'jpeg' });
  }
  if (best.size > 0 && best.size <= targetBytes) return best;

  // Выбираем формат для дальнейшего сжатия
  const mime = String(src0.type || '').toLowerCase();
  const ext = getFileExtension(String(src0.name || file.name || ''));
  const isPng = mime === 'image/png' || ext === 'png';
  const canWebp = supportsCanvasEncodeMime('image/webp');
  // Комментарий на русском:
  // Выбор формата:
  // - Safari/iOS: чаще безопаснее держать JPEG (меньше странных декод/память‑багов)
  // - Chrome/Edge/Firefox/Android: WebP даёт меньше трафика, если encode поддерживается.
  const allowWebp = canWebp && !safariLike;
  const preferWebpForPhoto = allowWebp && (src0.size > 900_000 || String(src0.type || '').toLowerCase() === 'image/jpeg');
  const format: 'jpeg' | 'webp' = allowWebp && (isPng || preferWebpForPhoto) ? 'webp' : 'jpeg';

  // Итеративно ужимаем до targetBytes
  let q = format === 'webp' ? 0.86 : 0.82;
  let w = maxWidth;
  let h = maxHeight;

  for (let i = 0; i < 7; i++) {
    try {
      const cand = await compressImage(src0, { maxWidth: w, maxHeight: h, quality: q, format });
      if (cand && cand.size > 0) {
        if (!best || cand.size < best.size) best = cand;
        if (cand.size <= targetBytes) return cand;
      }
    } catch {
      // ignore
    }

    // Стратегия: сначала падаем по quality, потом уменьшаем размеры.
    q = Math.max(0.58, q - (format === 'webp' ? 0.07 : 0.09));
    if (q <= 0.6) {
      w = Math.max(960, Math.round(w * 0.82));
      h = Math.max(960, Math.round(h * 0.82));
      q = format === 'webp' ? 0.84 : 0.8;
    }
  }

  return best && best.size > 0 ? best : src0;
}

/**
 * Оптимизация GIF для отправки в чат (с сохранением анимации).
 * Важно: canvas/WebP/JPEG для GIF не подходит — потеряется анимация.
 * Решение: gifsicle (WASM) — resize + оптимизация палитры/фреймов.
 */
export async function optimizeGifForChatUpload(
  file: File,
  options: {
    targetBytes?: number;
    maxWidth?: number;
    maxHeight?: number;
  } = {}
): Promise<File> {
  if (!file) throw new Error('Empty file');
  if (typeof window === 'undefined') return file;
  if (!isGifLikeFile(file)) return file;

  const env = getClientEnv();
  const isMobileLike = env.device === 'mobile' || env.device === 'tablet';
  const safariLike = isSafariLike(env);

  // Комментарий на русском: у GIF вес обычно намного выше, чем у JPEG/WebP.
  // Но PHP по умолчанию часто ограничен 2MB, поэтому держим targetBytes примерно как для обычных картинок.
  const defaultTarget = safariLike ? 1_200_000 : isMobileLike ? 1_400_000 : 1_900_000;
  const targetBytes = Math.max(250_000, Math.round(Number(options.targetBytes || 0) || defaultTarget));

  // Если и так пролезаем — не трогаем (сохраняем качество).
  if (file.size > 0 && file.size <= targetBytes) return file;

  // GIF для фона — не обязательно огромный по разрешению. Начинаем с умеренного размера и уменьшаем итеративно.
  const maxWidth = Math.max(240, Math.round(Number(options.maxWidth || 0) || (isMobileLike ? 720 : 960)));
  const maxHeight = Math.max(240, Math.round(Number(options.maxHeight || 0) || (isMobileLike ? 960 : 1280)));

  const baseName = (() => {
    const n = String(file.name || '').trim();
    const noExt = n.replace(/\.[^/.]+$/, '');
    return noExt || 'wallpaper';
  })();

  // Динамический import — wasm нужен только для GIF.
  const mod: any = await import('gifsicle-wasm-browser');
  const gifsicle = (mod && (mod.default || mod)) as any;
  if (!gifsicle || typeof gifsicle.run !== 'function') throw new Error('gifsicle-wasm-browser is not available');

  const inBuf = await file.arrayBuffer();

  let bestBlob: Blob | null = null;
  let bestSize = Number.POSITIVE_INFINITY;

  let w = maxWidth;
  let h = maxHeight;
  let colors = 256;
  let lossy = 40;

  const runOnce = async (args: string[]): Promise<Blob | null> => {
    const res = await gifsicle.run({
      input: [{ file: inBuf, name: 'input.gif' }],
      command: [...args, 'input.gif', '-o', '/out/output.gif'],
    });
    const out0: any = Array.isArray(res) ? res[0] : null;
    if (!out0) return null;
    // Некоторые версии возвращают Blob, некоторые File — приводим к Blob.
    const blob: Blob = out0 instanceof Blob ? out0 : new Blob([out0], { type: 'image/gif' });
    if (!blob || typeof (blob as any).size !== 'number') return null;
    return blob;
  };

  for (let i = 0; i < 10; i++) {
    const argsBase = [`--resize-fit ${w}x${h}`, `--colors ${colors}`, '-O3'];

    // Пробуем с lossy (обычно сильнее уменьшает вес). Если сборка gifsicle без lossy — упадёт, тогда пробуем без него.
    let out: Blob | null = null;
    try {
      out = await runOnce([...argsBase, `--lossy=${lossy}`]);
    } catch {
      try {
        out = await runOnce(argsBase);
      } catch {
        out = null;
      }
    }

    if (out && out.size > 0) {
      if (out.size < bestSize) {
        bestSize = out.size;
        bestBlob = out;
      }
      if (out.size <= targetBytes) {
        return new File([out], `${baseName}.gif`, { type: 'image/gif' });
      }
    }

    // Дальше уменьшаем: сначала палитра/losy, потом разрешение.
    if (colors > 32) colors = Math.max(32, Math.floor(colors / 2));
    lossy = Math.min(320, lossy + 35);
    // Комментарий на русском: GIF часто "не сжимается" без уменьшения размеров — уменьшаем постепенно.
    w = Math.max(240, Math.round(w * 0.86));
    h = Math.max(240, Math.round(h * 0.86));
  }

  return bestBlob ? new File([bestBlob], `${baseName}.gif`, { type: 'image/gif' }) : file;
}





